import { resolveIAiBrowserPublicServiceBaseUrl } from "@airange/shared";
import { transformGraphJsonForPreview } from "@airange/shared/topology-x6-preview";
import { Graph } from "@antv/x6";
import type { CSSProperties } from "react";
import type { ReadonlyTopologyNode } from "./readonly-topology-x6-types";

export const READONLY_TOPOLOGY_X6_WIDTH = 1180;
export const READONLY_TOPOLOGY_X6_NODE_WIDTH = 170;
export const READONLY_TOPOLOGY_X6_NODE_HEIGHT = 64;
export const READONLY_TOPOLOGY_X6_PADDING_X = 32;
export const READONLY_TOPOLOGY_X6_PADDING_Y = 24;
export const READONLY_TOPOLOGY_X6_LANE_HEIGHT = 112;

export function truncate(text: string, max = 20) {
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

export function buildNodeLabel(node: ReadonlyTopologyNode) {
  const lines = [truncate(node.name)];
  if (node.metaText) {
    for (const line of node.metaText
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 2)) {
      lines.push(truncate(line, 24));
    }
  }
  return lines.join("\n");
}

export function buildReadonlyTopologyGraphJsonViewerContainerStyle(
  minHeight: number,
): CSSProperties {
  return { width: "100%", height: minHeight, minHeight };
}

export function readReadonlyTopologyGraphJsonViewerSize(container: HTMLElement, minHeight: number) {
  const bounds = container.getBoundingClientRect();
  const parent = container.parentElement;
  const widthCandidates = [container.clientWidth, bounds.width, parent?.clientWidth].filter(
    (value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0,
  );
  const width = Math.floor(
    widthCandidates.length > 0 ? Math.min(...widthCandidates) : READONLY_TOPOLOGY_X6_WIDTH,
  );
  const height = Math.floor(container.clientHeight || bounds.height || minHeight);
  return { width, height };
}

export function resizeReadonlyTopologyGraph(
  graph: Pick<Graph, "resize">,
  container: HTMLElement,
  fallbackHeight: number,
) {
  const { width, height } = readReadonlyTopologyGraphJsonViewerSize(container, fallbackHeight);
  graph.resize(width, height);
}

export function fitReadonlyTopologyGraph(
  graph: Pick<Graph, "zoomToFit" | "centerContent">,
  padding: number,
) {
  graph.zoomToFit({ padding, maxScale: 1 });
  graph.centerContent();
}

function readClientEnv(key: string) {
  const env = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ||
    {}) as Record<string, string | undefined>;
  if (typeof env[key] === "string" && env[key]?.trim()) {
    return env[key]!.trim();
  }
  const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process?.env;
  if (typeof processEnv?.[key] === "string" && processEnv[key]?.trim()) {
    return processEnv[key]!.trim();
  }
  return "";
}

export function getAssetApiBaseUrl() {
  return resolveIAiBrowserPublicServiceBaseUrl("asset-center", "VITE_ASSET_BASE_URL", {
    deprecatedEnvKeys: ["VITE_ASSET_API_BASE_URL"],
    readClientEnv,
  });
}

export function buildTopologyNodePreviewPresentation(
  metadata: Record<string, unknown> | undefined,
) {
  const data =
    metadata?.data && typeof metadata.data === "object"
      ? (metadata.data as Record<string, unknown>)
      : {};
  const runtimeType = typeof data.runtime_type === "string" ? data.runtime_type.trim() : "";
  const implementation = typeof data.implementation === "string" ? data.implementation.trim() : "";
  const role = typeof data.role === "string" ? data.role.trim() : "";
  const accentColor =
    implementation === "router" || implementation === "switch"
      ? "#2563eb"
      : runtimeType === "container"
        ? "#16a34a"
        : role
          ? "#7c3aed"
          : "#334155";
  const metaText = [runtimeType || null, role || null].filter(Boolean).join(" / ");
  return {
    accentColor,
    labelText: typeof data.label === "string" && data.label.trim() ? data.label.trim() : "",
    metaText,
  };
}

export function ensurePreviewNodeRegistry() {
  try {
    Graph.registerNode("icon-label-preview", {
      inherit: "rect",
      width: 80,
      height: 90,
      markup: [
        { tagName: "rect", selector: "body" },
        { tagName: "image", selector: "image" },
        { tagName: "text", selector: "label" },
      ],
      attrs: {
        body: { stroke: "transparent", fill: "transparent" },
        image: { width: 64, height: 64, refX: 0.5, refX2: -32, y: 0 },
        label: {
          fontSize: 12,
          textAnchor: "middle",
          textVerticalAnchor: "top",
          refX: 0.5,
          refY: "100%",
          refY2: -20,
          fill: "var(--color-text)",
        },
      },
    });
  } catch {}
  try {
    Graph.registerNode("topology-node", {
      inherit: "rect",
      width: 180,
      height: 72,
      markup: [
        { tagName: "rect", selector: "body" },
        { tagName: "circle", selector: "accent" },
        { tagName: "text", selector: "label" },
        { tagName: "text", selector: "meta" },
      ],
      attrs: {
        body: {
          rx: 12,
          ry: 12,
          fill: "#ffffff",
          stroke: "#334155",
          strokeWidth: 1.5,
        },
        accent: {
          r: 5,
          cx: 16,
          cy: 16,
          fill: "#334155",
          stroke: "#334155",
        },
        label: {
          refX: 0.5,
          refY: 26,
          textAnchor: "middle",
          textVerticalAnchor: "middle",
          fontSize: 12,
          fontWeight: 600,
          fill: "#0f172a",
        },
        meta: {
          refX: 0.5,
          refY: 48,
          textAnchor: "middle",
          textVerticalAnchor: "middle",
          fontSize: 11,
          fill: "#64748b",
        },
      },
      propHooks(metadata) {
        const presentation = buildTopologyNodePreviewPresentation(metadata);
        return {
          ...metadata,
          attrs: {
            ...(metadata?.attrs && typeof metadata.attrs === "object"
              ? (metadata.attrs as Record<string, unknown>)
              : {}),
            body: {
              fill: "#ffffff",
              stroke: presentation.accentColor,
              strokeWidth: 1.5,
              rx: 12,
              ry: 12,
            },
            accent: {
              fill: presentation.accentColor,
              stroke: presentation.accentColor,
            },
            label: {
              text: presentation.labelText,
            },
            meta: {
              text: presentation.metaText,
            },
          },
        };
      },
    });
  } catch {}
}

export const __readonlyTopologyX6ViewerTestables = {
  getAssetApiBaseUrl,
  buildTopologyNodePreviewPresentation,
  buildReadonlyTopologyGraphJsonViewerContainerStyle,
  readReadonlyTopologyGraphJsonViewerSize,
  fitReadonlyTopologyGraph,
  resizeReadonlyTopologyGraph,
  transformGraphJsonForPreview,
};
